/**
 * 目录树工具：将扁平目录列表转换为 TreeSelect 所需的树形结构
 * @author dongdong.fdd
 * @date 2025/3/1
 */

export interface DirTreeNode {
  value: string;
  title: string;
  children?: DirTreeNode[];
}

export const SYSTEM_DIRS = ['全部目录', '默认目录', '__all__', '__default__'];
export const ACTIVE_DIRECTORY_STORAGE_KEY = 'imgcloud_active_directory';

export function isSystemDirectory(dir?: string): boolean {
  return Boolean(dir && SYSTEM_DIRS.includes(dir));
}

export function normalizeShareDirectory(dir?: string): string {
  return dir === '__default__' || dir === '默认目录' ? 'default' : (dir || '');
}

export function normalizeDirectoryName(value: string): string {
  return value
    .trim()
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .map((part) => part.trim().replace(/\s+/g, '_'))
    .filter(Boolean)
    .join('/');
}

export function containsEmoji(value: string): boolean {
  for (let index = 0; index < value.length;) {
    const codePoint = value.codePointAt(index);
    if (codePoint === undefined) return false;

    if (isEmojiCodePoint(codePoint)) return true;
    index += codePoint > 0xFFFF ? 2 : 1;
  }
  return false;
}

function isEmojiCodePoint(codePoint: number): boolean {
  return (codePoint >= 0x1F000 && codePoint <= 0x1FAFF)
    || (codePoint >= 0x2600 && codePoint <= 0x27FF)
    || [0x00A9, 0x00AE, 0x203C, 0x2049, 0x20E3, 0x2122, 0x2139, 0x3030, 0x303D, 0x3297, 0x3299]
      .includes(codePoint);
}

export function composeDirectoryPath(parent: string, name: string): string {
  const normalizedName = normalizeDirectoryName(name);
  const normalizedParent = parent.replace(/\/+$/g, '');
  return normalizedParent ? `${normalizedParent}/${normalizedName}` : normalizedName;
}

export function sortDirectories(dirs: string[]): string[] {
  const uniqueDirs = Array.from(new Set(dirs.filter(Boolean)));
  const systemDirs = SYSTEM_DIRS.filter((dir) => uniqueDirs.includes(dir));
  const userDirs = uniqueDirs
    .filter((dir) => !SYSTEM_DIRS.includes(dir))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));

  return [...systemDirs, ...userDirs];
}

export function getStoredActiveDirectory(): string {
  try {
    return localStorage.getItem(ACTIVE_DIRECTORY_STORAGE_KEY) || '';
  } catch (error) {
    return '';
  }
}

export function saveActiveDirectory(dir: string): void {
  try {
    if (dir) {
      localStorage.setItem(ACTIVE_DIRECTORY_STORAGE_KEY, dir);
    } else {
      localStorage.removeItem(ACTIVE_DIRECTORY_STORAGE_KEY);
    }
  } catch (error) {
    // Ignore storage failures so directory switching still works in restricted browsers.
  }
}

/**
 * 将扁平目录列表转换为树形结构
 * @param flatDirs 扁平目录列表，如 ["全部目录", "默认目录", "images", "images/2024", "images/2024/q1"]
 * @returns TreeSelect 所需的 treeData
 */
export function buildDirTree(flatDirs: string[]): DirTreeNode[] {
  if (!flatDirs || flatDirs.length === 0) {
    return [];
  }

  const systemNodes: DirTreeNode[] = [];
  const userPaths: string[] = [];

  for (const d of flatDirs) {
    if (SYSTEM_DIRS.includes(d)) {
      systemNodes.push({ value: d, title: d });
    } else {
      userPaths.push(d);
    }
  }

  const treeMap = new Map<string, DirTreeNode>();

  for (const path of userPaths) {
    const parts = path.split('/').filter(Boolean);
    for (let i = 0; i < parts.length; i++) {
      const fullPath = parts.slice(0, i + 1).join('/');
      if (!treeMap.has(fullPath)) {
        treeMap.set(fullPath, {
          value: fullPath,
          title: parts[i],
          children: [],
        });
      }
    }
  }

  const childIds = new Set<string>();
  const allPaths = Array.from(treeMap.keys()).sort((a, b) => a.split('/').length - b.split('/').length);

  for (const path of allPaths) {
    const parts = path.split('/').filter(Boolean);
    if (parts.length <= 1) continue;

    const node = treeMap.get(path);
    const parentPath = parts.slice(0, -1).join('/');
    const parent = treeMap.get(parentPath);
    if (!node || !parent?.children) continue;

    if (!parent.children.some((c) => c.value === path)) {
      parent.children.push(node);
    }
    childIds.add(path);
  }

  const rootNodes = Array.from(treeMap.values()).filter((n) => !childIds.has(n.value));
  return [...systemNodes, ...rootNodes];
}

/**
 * 递归替换树节点的 title
 */
export function mapDirTreeTitles(
  nodes: DirTreeNode[],
  formatLabel: (value: string) => string
): DirTreeNode[] {
  return nodes.map((node) => ({
    ...node,
    title: formatLabel(node.value),
    children: node.children?.length
      ? mapDirTreeTitles(node.children, formatLabel)
      : undefined,
  }));
}
